import { useState, useEffect, useCallback } from 'react';
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush,
  registerServiceWorker
} from '../lib/pushNotifications';

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check initial state
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const supported = isPushSupported();
        setIsSupported(supported);
        
        if (supported) {
          setPermission(getNotificationPermission());
          
          // Register service worker
          await registerServiceWorker();
          
          // Check subscription status
          const subscribed = await isSubscribedToPush();
          setIsSubscribed(subscribed);
        }
      } catch (err) {
        console.error('Error checking push status:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkStatus();
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await subscribeToPush();
      
      if (result.success) {
        setIsSubscribed(true);
        setPermission('granted');
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await unsubscribeFromPush();
      
      if (result.success) {
        setIsSubscribed(false);
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Toggle subscription
  const toggle = useCallback(async () => {
    if (isSubscribed) {
      return await unsubscribe();
    } else {
      return await subscribe();
    }
  }, [isSubscribed, subscribe, unsubscribe]);

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    toggle
  };
}

export default usePushNotifications;
